import * as STD  from '@emersya/mpa-std';

abstract class PropertyOperation<
	TDescriptor extends PropertyOperation.IDescriptor<any>
> {
	static create(desc: PropertyOperation.TDescriptor): PropertyOperation.TOperation {
		if (desc.type === 'read') {
			return new PropertyOperation.ReadOperation(desc);
		} else if (desc.type === 'call') {
			return new PropertyOperation.CallOperation(desc);
		}
		throw STD.errors.user('Not implemented type', {
			type: (desc as any).type,
			descriptor: desc
		});
	};

	static async createAndExec(
		desc: PropertyOperation.TDescriptor,
		host: Component<any, any>,
		event: STD.events.Event<any, Component<any, any>>
	) {
		const s = this.create(desc);
		return (await s.execute(host, event));
	};

	readonly type: TDescriptor['type'];
	readonly componentPath!: Path.TPath;
	readonly propertyPath: STD.object.TPropertyPath;

	abstract execute(
		host: Component<any, any>,
		event: STD.events.Event<any, Component<any, any>>
	): Promise<unknown>;

	getComponent(host: Component<any, any>) {
		if (this.componentPath.type === 'relative') {
			return (host.component_find(this.componentPath));
		}
		if (!Manager.root) {
			throw STD.errors.library('Not root component!', {});
		}
		return (Manager.root.component_find(this.componentPath));
	};

	protected constructor(descriptor: PropertyOperation.TDescriptor) {
		this.type = descriptor.type;
		this.propertyPath = STD.object.PropertyPath.create(descriptor.propertyPath);
		const componentPathDescriptor = Object.getOwnPropertyDescriptor(descriptor, 'componentPath');
		if (componentPathDescriptor && componentPathDescriptor.get) {
			Object.defineProperty(this, 'componentPath', componentPathDescriptor);
		} else {
			this.componentPath = typeof descriptor.componentPath === 'string' ? Path.parse(descriptor.componentPath) : descriptor.componentPath;
		}
	};
};

namespace PropertyOperation {
	export interface IDescriptor<
		TType extends PropertyOperation.TType
	> {
		readonly type: TType;
		readonly componentPath: Path.TPathRaw | Path.TPath;
		readonly propertyPath: string;
	};

	export class ReadOperation<
		TDescriptor extends ReadOperation.IDescriptor<TType>,
		TType extends 'read' | 'call'
	> extends PropertyOperation<TDescriptor> {
		async execute(
			host: Component<any, any>,
			event: STD.events.Event<any, Component<any, any>>
		) {
			void event;
			const fromComponent = this.getComponent(host);
			if (!fromComponent) {
				return (undefined);
			}
			return (await this.propertyPath.evaluate(fromComponent));
		};
	};

	export namespace ReadOperation {
		export interface IDescriptor<
			TType extends PropertyOperation.TType = 'read'
		> extends PropertyOperation.IDescriptor<TType> {

		};

	}

	export class CallOperation extends ReadOperation<CallOperation.IDescriptor, 'call'> {
		parameters: STD.object.JSON[];

		override async execute(
			host: Component<any, any>,
			event: STD.events.Event<any, Component<any, any>>
		) {
			const method = await super.execute(host, event);
			if (!method || (typeof method !== 'function')) {
				throw STD.errors.user('Method not found or invalid type!', {
					propertyOperation: this,
					event,
					method
				});
			}
			const parameters = <unknown[]>await Manager.mapFormalEventActionParameterToEffective(
				event,
				this.parameters,
				host
			);
			try {
				const result = await method(...parameters, event);
				return (result);
			} catch (error: any) {
				throw STD.errors.bindAsBound('Error during call execution!', {
					Event: STD.events.Event,
					host,
					method,
					parameters
				}, error);
			}
		};

		constructor(descriptor: CallOperation.IDescriptor) {
			super(descriptor);
			this.parameters = descriptor.parameters;
		};
	};
	export namespace CallOperation {
		export interface IDescriptor extends PropertyOperation.ReadOperation.IDescriptor<'call'> {
			readonly parameters: STD.object.JSON[];
		};

	}
	;
	export type TType = 'read' | 'call';
	export type TOperation = ReadOperation<ReadOperation.IDescriptor, 'read'> | CallOperation;
	export type TDescriptor = ReadOperation.IDescriptor | CallOperation.IDescriptor;

}
export default PropertyOperation;
import Component from './Component';
import Path      from './Path';
import Manager   from './Manager';
